import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, Package, Handshake, Image as ImageIcon, Mail } from "lucide-react";
import { HERO_SLIDES } from "../mock";
import useSEO from "../hooks/useSEO";

const QUICK_LINKS = [
  { to: "/company", label: "About us", desc: "Who we are and what drives us", icon: Building2 },
  { to: "/catalog", label: "Products", desc: "Explore our commodity verticals", icon: Package },
  { to: "/partners", label: "Partners", desc: "Trusted by global leaders", icon: Handshake },
  { to: "/gallery", label: "Gallery", desc: "A look at our operations", icon: ImageIcon },
  { to: "/contacts", label: "Contacts", desc: "Get in touch with our team", icon: Mail },
];

const Home = () => {
  useSEO({
    title: "Commodity Trading in Dubai",
    description: "Prime Trade L.L.C — FZ is a Dubai-based commodity trading company supplying Urea, MOP, DAP, Sulphur, Diesel EN 590 and agricultural commodities worldwide.",
  });
  const [slide, setSlide] = useState(0);
  const videoRefs = useRef([]);

  useEffect(() => {
    const id = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 6500);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    // Ensure all hero videos are muted and can autoplay
    videoRefs.current.forEach((videoEl) => {
      if (videoEl) {
        videoEl.muted = true;
        videoEl.defaultMuted = true;
        videoEl.play().catch((err) => {
          console.error("Hero video autoplay failed:", err);
        });
      }
    });
  }, []);

  return (
    <main>
      {/* HERO */}
      <section className="pt-hero">
        {HERO_SLIDES.map((s, i) => (
          <div
            key={i}
            className={`pt-hero-slide ${i === slide ? "active" : ""}`}
          >
            {s.type === "video" ? (
              <video
                ref={(el) => (videoRefs.current[i] = el)}
                src={s.video}
                poster={s.image}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <div
                className="absolute inset-0 w-full h-full bg-cover bg-center"
                style={{ backgroundImage: `url(${s.image})` }}
              />
            )}
          </div>
        ))}
        <div className="relative z-10 h-full max-w-[1280px] mx-auto px-6 flex items-center">
          <div className="pt-hero-content">
            <div className="pt-hero-eyebrow">{HERO_SLIDES[slide].eyebrow}</div>
            <h1>{HERO_SLIDES[slide].title}</h1>
            <div className="subtitle">{HERO_SLIDES[slide].subtitle}</div>
            <Link to={HERO_SLIDES[slide].cta.href} className="btn-red">
              {HERO_SLIDES[slide].cta.label}
            </Link>
          </div>
        </div>
        <div className="pt-hero-dots">
          {HERO_SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setSlide(i)}
              className={i === slide ? "active" : ""}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Navigation cards */}
      <section className="pt-section">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="pt-eyebrow">Explore</div>
            <h2 className="pt-h2">Where would you like to go?</h2>
            <div className="pt-divider mx-auto" />
            <p className="text-[15px] text-[#4a4a4a] leading-relaxed">
              Prime Trade L.L.C &mdash; FZ connects producers and buyers across continents. Browse our world below.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {QUICK_LINKS.map((q) => {
              const Icon = q.icon;
              return (
                <Link
                  key={q.to}
                  to={q.to}
                  className="group block bg-white border border-[#ececec] hover:border-[#e30613] p-7 rounded-sm transition-all hover:-translate-y-1 hover:shadow-[0_18px_38px_rgba(0,0,0,0.08)]"
                >
                  <div className="flex items-start justify-between mb-5">
                    <div className="w-12 h-12 bg-[#fef2f3] text-[#e30613] flex items-center justify-center rounded-sm">
                      <Icon size={22} />
                    </div>
                    <ArrowRight
                      size={18}
                      className="text-[#7a7a7a] group-hover:text-[#e30613] group-hover:translate-x-1 transition-all"
                    />
                  </div>
                  <h3 className="text-[20px] font-semibold text-[#2c2c2c] mb-1">{q.label}</h3>
                  <p className="text-sm text-[#7a7a7a]">{q.desc}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
